import React, { Component } from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import { GlobalState } from "../../types/globalState";
import { WithStyles } from "@material-ui/core";
import CompetitionActions from "../../actions/competitions";
import MyTable, { Column, TableAction, TableState } from "../SharedComponents/MyTable";
import { showDialog } from "../SharedComponents/Dialog";
import CompetitionSetup from "./CompetitionSetup";
import { Competition, IndexedCompetitions } from "../../types/competition";
import ListStyles from "../../SharedStyles/ListStyle";
import { GameRules } from "types/competition";
import { showSnackbar } from "../SharedComponents/Notifier";
import { Rules, User } from "../../types/user";
import { isGameStandard } from "utils";
import CustomSwitch from "components/SharedComponents/CustomSwitch";
import CompetitionApi from "../../api/competitions";

interface Props extends WithStyles {
  deleteCompetition(id: string, active?: boolean): Promise<unknown> | void;
  competitionsObj: IndexedCompetitions;
  loading: boolean;
  user: User;
}

interface State {
  openDialog: boolean;
  tableRows: Competition[];
  tableCount: number;
}

const defaultTableState: TableState = {
  page: 0,
  pageSize: 50,
  sort: { field: "name", dir: "asc" },
  query: undefined,
  selectedRows: {},
};

class CompetitionsList extends Component<Props, State> {
  private lastTableState: TableState = defaultTableState;

  constructor(props: Props) {
    super(props);
    this.state = {
      openDialog: false,
      tableRows: [],
      tableCount: 0,
    };
  }

  componentDidMount() {
    this.loadList(this.lastTableState);
  }

  private queryParamForRequest(q?: string) {
    const t = (q && String(q).trim()) || "";
    if (t.length < 2) return undefined;
    return t;
  }

  loadList = (state: TableState) => {
    this.lastTableState = state;
    const sort = state.sort?.field ? state.sort : { field: "name", dir: "asc" as const };
    return CompetitionApi.get(
      state.page + 1,
      state.pageSize,
      undefined,
      sort.field,
      sort.dir,
      this.queryParamForRequest(state.query),
    )
      .then((res: { data: { items: Competition[]; count: number } }) => {
        this.setState({ tableRows: res.data.items || [], tableCount: res.data.count ?? 0 });
      })
      .catch(() => {
        this.setState({ tableRows: [], tableCount: 0 });
      });
  };

  onSetupDialogClose = () => {
    this.loadList(this.lastTableState);
  };

  private getCompetitionById = (id: string): Competition | undefined => {
    return this.state.tableRows.find(r => r._id === id) || (this.props.competitionsObj as any)[id];
  };

  prepareData = (items: any) => {
    return items.map((item: Competition) => {
      let gender = "Undefined";
      let { eligibility } = item;
      if (eligibility[0].exist && eligibility[1].exist) {
        gender = "Mixed";
      } else if (eligibility[0].exist) {
        gender = "Men";
      } else if (eligibility[1].exist) {
        gender = "Ladies";
      }

      const { name, lastName } = item?.owner || {};

      return {
        ...item,
        type: item.type === GameRules.CUSTOM ? "Custom" : `Standard (${item.type})`,
        owner: name && lastName ? `${name} ${lastName}` : `${this.props.user.name} ${this.props.user.lastName}`,
        gender,
        active: item.active === undefined ? true : item.active,
      };
    });
  };

  _onTableChange = (action: TableAction, state: TableState) => {
    this.loadList(state);
  };

  _checkPermission = (cmp: Competition) => {
    if (isGameStandard(cmp.type) && this.props.user.rule !== Rules.Admin) {
      showSnackbar("You are not allowed to do this action.");
      return false;
    }
    return true;
  };

  _onEditItem = (rowId: string) => {
    const cmp = this.getCompetitionById(rowId);
    if (cmp && this._checkPermission(cmp)) {
      showDialog(<CompetitionSetup edit={cmp} />, false, true, false, false, undefined, undefined, this.onSetupDialogClose);
    }
  };

  _onDuplicateItem = (rowId: string) => {
    const cmp = this.getCompetitionById(rowId);
    if (cmp) {
      showDialog(<CompetitionSetup copy={cmp} />, false, true, false, false, undefined, undefined, this.onSetupDialogClose);
    }
  };

  _onDeleteItems = (rowsIds: string[]) => {
    const toDelete: string[] = [];
    for (const id of rowsIds) {
      const cmp = this.getCompetitionById(id);
      if (cmp && this._checkPermission(cmp)) toDelete.push(id);
    }
    if (toDelete.length === 0) return;
    const { deleteCompetition } = this.props;
    const run = toDelete.map(id => {
      const p = deleteCompetition(id);
      return p == null || typeof (p as Promise<unknown>).then !== "function" ? Promise.resolve() : p;
    });
    void Promise.all(run).then(() => this.loadList(this.lastTableState));
  };

  _onAddItem = () => {
    showDialog(<CompetitionSetup />, false, true, false, false, undefined, undefined, this.onSetupDialogClose);
  };

  render() {
    const { classes, loading, user } = this.props;
    const { tableRows, tableCount } = this.state;

    const columns: Column[] = [
      {
        field: "name",
        title: "Name",
      },
      {
        field: "type",
        title: "Rule Type",
      },
      {
        field: "scoringFormat",
        title: "Scoring Format",
      },

      {
        field: "gender",
        title: "Gender",
      },
      ...(user.rule === Rules.Admin
        ? [
            {
              field: "owner",
              title: "Owner",
            },
            {
              field: "active",
              title: "Active",
              handler: (field: any, item?: any) => {
                return (
                  <CustomSwitch
                    checked={field}
                    onChange={e => {
                      const p = this.props.deleteCompetition(item._id, e.target.checked);
                      const pr = p == null || typeof (p as Promise<unknown>).then !== "function" ? Promise.resolve() : p;
                      void pr.then(() => this.loadList(this.lastTableState));
                    }}
                  />
                );
              },
            },
          ]
        : []),
    ];

    return (
      <div className={classes.paper}>
        <MyTable
          title={"Game Rules"}
          loading={loading}
          count={tableCount}
          data={this.prepareData(tableRows)}
          defaultOrderBy={"name"}
          columns={columns}
          selectable
          disabledRow
          searchEnabled
          serverSidePagination
          onTableChange={this._onTableChange}
          onEdit={this._onEditItem}
          onDelete={this._onDeleteItems}
          onAdd={this._onAddItem}
          onDuplicate={this._onDuplicateItem}
        />
      </div>
    );
  }
}

const styles = ListStyles({});
const mapStateToProps = (state: GlobalState) => {
  const { competitions } = state.competitions;
  const { user } = state.auth;
  const { loading } = state.general;
  return {
    competitionsObj: competitions,
    user,
    loading,
  };
};

export default connect(mapStateToProps, {
  deleteCompetition: CompetitionActions.delete,
})(withStyles(styles)(CompetitionsList));
